import {AccountInfo} from '@azure/msal-browser';
import {roleHierarchy, UserPermissions, UserRole} from '../domain/auth.types';

/**
 * Extract roles from MSAL account token claims
 * @param account MSAL account info
 * @returns Array of user roles
 */
export function extractRolesFromAccount(account: AccountInfo | null): UserRole[] {
    if (!account?.idTokenClaims) {
        return [];
    }
    
    const roleClaim = account.idTokenClaims['roles'] as string | string[] | undefined;
    
    if (!roleClaim) {
        return [];
    }

    const roleStrings = Array.isArray(roleClaim) ? roleClaim : [roleClaim];
    
    // Filter and convert to UserRole enum values
    return roleStrings
        .filter((role): role is UserRole => Object.values(UserRole).includes(role as UserRole))
        .map(role => role as UserRole);
}

/**
 * Check if user has a specific role, considering role hierarchy
 * @param userRoles User's actual roles
 * @param requiredRole Role to check for
 * @returns True if user has the role or a higher role
 */
export function hasRole(userRoles: UserRole[], requiredRole: UserRole): boolean {
    // Check if any of the user's roles grant access to the required role
    return userRoles.some(userRole => 
        roleHierarchy[requiredRole].includes(userRole)
    );
}

/**
 * Check if user has any of the specified roles
 * @param userRoles User's actual roles
 * @param requiredRoles Roles to check for
 * @returns True if user has at least one of the roles
 */
export function hasAnyRole(userRoles: UserRole[], requiredRoles: UserRole[]): boolean {
    return requiredRoles.some(role => hasRole(userRoles, role));
}

/**
 * Create a UserPermissions object from user roles
 * @param userRoles User's roles
 * @returns UserPermissions object with helper methods and flags
 */
export function createUserPermissions(userRoles: UserRole[]): UserPermissions {
    return {
        roles: userRoles,
        hasRole: (role: UserRole) => hasRole(userRoles, role),
        hasAnyRole: (roles: UserRole[]) => hasAnyRole(userRoles, roles),
        canAccessAdminFeatures: hasRole(userRoles, UserRole.Admin),
        canAccessSupportFeatures: hasRole(userRoles, UserRole.Support),
        canAccessViewerFeatures: hasRole(userRoles, UserRole.Viewer)
    };
}

/**
 * Get user permissions from account
 * @param account MSAL account info
 * @returns UserPermissions object
 */
export function getUserPermissions(account: AccountInfo | null): UserPermissions {
    if (!account) {
        return createUserPermissions([]);
    }

    // Extract roles and create permissions
    const roles = extractRolesFromAccount(account);
    return createUserPermissions(roles);
}

export function formatRoleDisplay(role: UserRole): string {
    return role.toString().split('.').pop() || role.toString();
}

export function getRoleLabel(role: UserRole): string {
    switch (role) {
        case UserRole.Admin:
            return 'Administrator';
        case UserRole.Support:
            return 'Support';
        case UserRole.Viewer:
            return 'Viewer';
        default:
            return 'authorized user';
    }
}

export function capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}